const API = process.env.REACT_APP_BACKEND_URL;

// Import all bundled data
import chronicDiseases from '../data/chronicDiseases.json';
import autoimmuneDiseases from '../data/autoimmuneDiseases.json';
import chronicMapData from '../data/chronicMapData.json';
import autoimmuneMapData from '../data/autoimmuneMapData.json';
import vaccineCoverage from '../data/vaccineCoverage.json';
import vaccineByAge from '../data/vaccineByAge.json';
import vaccineMapData from '../data/vaccineMapData.json';
import vaccineMyths from '../data/vaccinMyths.json';
import vaccineSimulation from '../data/vaccineSimulation.json';
import flashcards from '../data/flashcards.json';
import flashcardDecks from '../data/flashcardDecks.json';
import mythBuster from '../data/mythBuster.json';
import landmarkStudies from '../data/landmarkStudies.json';
import recommendedBooks from '../data/recommendedBooks.json';
import riskCalculatorBaseline from '../data/riskCalculatorBaseline.json';
import quickStats from '../data/quickStats.json';
import analogies from '../data/analogies.json';
import dataCoverage from '../data/dataCoverage.json';
import topics from '../data/topics.json';
import stories from '../data/stories.json';
import glossary from '../data/glossary.json';
import chronicTrendsByDisease from '../data/chronicTrends.json';
import chronicDemographicsByDisease from '../data/chronicDemographics.json';
import chronicRiskFactors from '../data/chronicRiskFactors.json';
import complexConditions from '../data/complexConditions.json';
import careerPaths from '../data/careerPaths.json';

// Map API paths to bundled fallback data
const FALLBACK_MAP = {
  '/api/chronic/diseases': chronicDiseases,
  '/api/autoimmune/diseases': autoimmuneDiseases,
  '/api/vaccines/coverage': vaccineCoverage,
  '/api/vaccines/by-age': vaccineByAge,
  '/api/vaccines/map-data': vaccineMapData,
  '/api/vaccines/myths': vaccineMyths,
  '/api/vaccines/simulation': vaccineSimulation,
  '/api/education/flashcards': flashcards,
  '/api/education/myth-buster': mythBuster,
  '/api/education/landmark-studies': landmarkStudies,
  '/api/education/recommended-books': recommendedBooks,
  '/api/risk-calculator': riskCalculatorBaseline,
  '/api/education/quick-stats': quickStats,
  '/api/education/analogies': analogies,
  '/api/data-coverage': dataCoverage,
  '/api/education/topics': topics,
  '/api/education/stories': stories,
  '/api/education/glossary': glossary,
  '/api/chronic/risk-factors': chronicRiskFactors,
  // /api/chronic/trends and /api/chronic/demographics are handled per-disease in getDynamicFallback below.
  '/api/complex-conditions': complexConditions,
  '/api/education/career-paths': careerPaths,
};

// Dynamic fallbacks for parameterized routes
function getDynamicFallback(path) {
  const chronicMapMatch = path.match(/\/api\/chronic\/map-data\?disease=(\w+)/);
  if (chronicMapMatch) {
    const disease = chronicMapMatch[1];
    const data = chronicMapData[disease] || [];
    return { data, disease, source: 'bundled' };
  }

  const autoimmuneMapMatch = path.match(/\/api\/autoimmune\/map-data\?disease=(\w+)/);
  if (autoimmuneMapMatch) {
    const disease = autoimmuneMapMatch[1];
    const data = autoimmuneMapData[disease] || [];
    return { data, disease, source: 'bundled' };
  }

  // Per-disease trends — empty array for diseases without verified longitudinal data
  // (matches backend behavior; UI hides the chart when trends.length === 0).
  const chronicTrendsMatch = path.match(/\/api\/chronic\/trends\?disease=(\w+)/);
  if (chronicTrendsMatch) {
    const disease = chronicTrendsMatch[1];
    const trends = chronicTrendsByDisease[disease] || [];
    return { trends, disease, source: 'bundled' };
  }

  // Per-disease demographics
  const chronicDemoMatch = path.match(/\/api\/chronic\/demographics\?disease=(\w+)/);
  if (chronicDemoMatch) {
    const disease = chronicDemoMatch[1];
    return chronicDemographicsByDisease[disease] || chronicDemographicsByDisease.diabetes || null;
  }

  const vaccCoverageMatch = path.match(/\/api\/vaccines\/coverage\?state=(\w+)/);
  if (vaccCoverageMatch) {
    return vaccineCoverage;
  }

  const riskCalcMatch = path.match(/\/api\/risk-calculator\?/);
  if (riskCalcMatch) {
    return riskCalculatorBaseline;
  }

  const flashcardDeckMatch = path.match(/\/api\/education\/flashcards\?deck_id=(\w+)/);
  if (flashcardDeckMatch) {
    const deckId = flashcardDeckMatch[1];
    return flashcardDecks[deckId] || null;
  }

  return null;
}

/**
 * Fetch data from API with automatic fallback to bundled data.
 * Usage: fetchWithFallback('/api/chronic/diseases').then(data => ...)
 */
export async function fetchWithFallback(path) {
  // Try live API first
  if (API) {
    try {
      const url = path.startsWith('http') ? path : `${API}${path}`;
      const res = await fetch(url, { signal: AbortSignal.timeout(5000) });
      if (res.ok) return await res.json();
    } catch {
      // API unavailable, fall through to bundled data
    }
  }

  // Try dynamic fallback FIRST (for paths with query params like ?deck_id=xxx)
  const dynamicFallback = getDynamicFallback(path);
  if (dynamicFallback) return dynamicFallback;

  // Then try static fallback (strip query params for lookup)
  const basePath = path.split('?')[0];
  const staticFallback = FALLBACK_MAP[basePath];
  if (staticFallback) return staticFallback;

  return null;
}

// GEO URL: use local bundled file first, CDN as fallback
export const GEO_URL_LOCAL = '/states-10m.json';
export const GEO_URL_CDN = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';
